import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from "@headlessui/react";
import axios from "axios";
import React from "react";
import { getRootDomain } from "../../helpers";
import { getUrlForBookings, getUrlForUsers } from "../../utils/confighelpers";

/**
 * A member signed in on a club's address, but isn't a member of that club (yet).
 * Offer to join this club (addMembers as the member: club membership, customer in the club's payment account and
 * the club's free trial credit, same as the sign-up page) or to go to a club they already belong to.
 * Removed members are refused by the server ("You can't join … online. Please contact …").
 *
 * props: offer = { userData (login response, with token), clubName, clubLink }, onJoined(freshUserData), onClose
 */
const clubNameOf = (club) => club?.business_detail?.myBusinessProfile?.businessName || club?.business_detail?.fullName || "your club";

export default function JoinClubDialog({ offer, onJoined, onClose }) {
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState("");
  const open = !!offer;
  const user = offer?.userData || {};
  const clubName = offer?.clubName || "this club";
  const otherClubs = (user.myClubs || []).filter((c) => c.myBookingLink && c.myBookingLink !== offer?.clubLink);
  const firstOther = otherClubs[0];

  React.useEffect(() => {
    setError("");
    setBusy(false);
  }, [offer]);

  const join = async () => {
    setBusy(true);
    setError("");
    try {
      const headers = { "x-access-token": user.token };
      const res = await axios.post(
        `${getUrlForBookings()}addMembers`,
        {
          username: user.username,
          countryCode: user.countryCode,
          phoneNumber: user.phoneNumber,
          fullName: user.fullName,
          email: user.email,
          actionType: "free_trial_credit",
          myBookingLink: offer.clubLink,
        },
        { headers }
      );
      if (res?.data?.statusCode !== 200) {
        const msg = typeof res?.data?.message === "string" ? res.data.message : "";
        setError(
          msg.indexOf("payment details") !== -1
            ? `${clubName} isn't taking new members online yet. Please contact ${clubName}.`
            : msg || "Something went wrong. Please try again."
        );
        setBusy(false);
        return;
      }
      // Fresh profile, so this club is in myClubs.
      let fresh = user;
      try {
        const info = await axios.get(`${getUrlForUsers()}getUserInfo`, { headers });
        if (info?.data?.statusCode === 200 && info.data.data) fresh = { ...info.data.data, token: user.token };
      } catch (e) {
        // carry on with the login data
      }
      onJoined(fresh);
    } catch (e) {
      setError("Couldn't reach the server. Please try again.");
      setBusy(false);
    }
  };

  const goToMyClub = () => {
    if (!firstOther) return;
    window.location.href = `${window.location.protocol}//${firstOther.myBookingLink}.${getRootDomain()}/`;
  };

  return (
    <Dialog open={open} onClose={() => !busy && onClose()} className="relative z-[60]">
      <DialogBackdrop transition className="fixed inset-0 bg-[#1d1320]/45 transition-opacity duration-200 data-[closed]:opacity-0" />
      <div className="fixed inset-0 flex items-end justify-center p-0 sm:items-center sm:p-4">
        <DialogPanel transition className="w-full max-w-md rounded-t-3xl bg-white p-6 shadow-2xl transition duration-200 data-[closed]:translate-y-4 data-[closed]:opacity-0 sm:rounded-3xl">
          <div className="flex items-start gap-4">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-[#F3E9FF] text-indigo-bz" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
                <circle cx="9" cy="8" r="4" />
                <path d="M2 21a7 7 0 0 1 14 0M19 8v6M16 11h6" />
              </svg>
            </span>
            <div className="min-w-0">
              <DialogTitle className="text-lg font-black text-gray-900">Join {clubName}?</DialogTitle>
              <p className="mt-1 text-sm text-gray-600">
                You're signed in{user.fullName ? ` as ${user.fullName.split(" ")[0]}` : ""}, but you're not a member of {clubName} yet. Join now to book classes here.
              </p>
              <p className="mt-2 text-xs text-gray-500">{clubName} will see your name, email and mobile number.</p>
            </div>
          </div>
          {error && (
            <div className="mt-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700" role="alert">{error}</div>
          )}
          <div className="mt-6 flex flex-col gap-2">
            <button
              type="button"
              onClick={join}
              disabled={busy}
              className="flex items-center justify-center gap-2 rounded-2xl bg-brown-coffee px-5 py-3 text-sm font-extrabold text-white hover:bg-light-brown-coffee disabled:opacity-60"
            >
              {busy && <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />}
              Join {clubName}
            </button>
            {firstOther && (
              <button type="button" onClick={goToMyClub} disabled={busy} className="rounded-2xl border border-gray-200 px-5 py-3 text-sm font-extrabold text-gray-900 hover:bg-gray-50 disabled:opacity-50">
                Go to {clubNameOf(firstOther)}
              </button>
            )}
            <button type="button" onClick={onClose} disabled={busy} className="rounded-2xl px-5 py-2.5 text-sm font-bold text-gray-500 hover:text-gray-900 disabled:opacity-50">
              Not now
            </button>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
}
